/**
 * Shared visual primitives for the recorded-video cursor overlays.
 *
 * Both cursor implementations — the driver-driven {@link CursorOverlay} (ref-based
 * actions, glides to each target before acting) and the autonomous in-page
 * {@link AutonomousCursor} (fast-drive `run_code`, reacts to real DOM events) —
 * draw the SAME arrow + ripple/glow in the SAME Cofactor brand. Keeping the SVG,
 * CSS, element id, and palette here is the single source of truth so the two
 * paths can never drift visually.
 *
 * Colors are baked constants, not caller-supplied — nothing external flows into
 * the injected SVG/CSS, so there is no markup/CSS-injection surface.
 */

/** Cofactor brand primary — cursor arrow fill. */
const CURSOR_COLOR = '#2f6fed';
/** Cofactor brand primary (lifted) — click ripple. Matches the existing type-glow blue. */
const RIPPLE_COLOR = '#3b82f6';

/** Shared DOM id for the injected cursor element (one element, both paths reuse it). */
export const CURSOR_ELEMENT_ID = '__chatsdetCursor';

/**
 * Host tag for the shadow-root overlay used by the anticipatory cue runtime.
 *
 * A hyphenated (valid custom-element) name rather than a `div`, so the host is
 * trivially distinguishable from application markup and can never be mistaken for
 * a layout element. Deliberately NOT registered via `customElements.define` — an
 * undefined-but-valid custom element still accepts `attachShadow`, and staying out
 * of the registry keeps us invisible to pages that enumerate it.
 */
export const CURSOR_HOST_TAG = 'cofactor-cursor';

/** `#rrggbb` → `rgba(r, g, b, a)`. Operates only on the baked constants above. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** The arrow-cursor SVG in the Cofactor brand color. */
export function buildCursorSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
  <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="${CURSOR_COLOR}" stroke="#000" stroke-width="1.5" stroke-linejoin="round"/>
</svg>`;
}

/** The highlight stylesheet (click ripple + type glow + hover frame) in brand colors. */
export function buildHighlightCss(): string {
  return `
  @keyframes chatsdet-ripple {
    0% { transform: translate(-50%, -50%) scale(0.4); opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
  }
  @keyframes chatsdet-halo-in {
    0% { opacity: 0; transform: scale(1.06); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes chatsdet-glow {
    0% { box-shadow: 0 0 8px 3px rgba(59, 130, 246, 0.95); }
    50% { box-shadow: 0 0 24px 8px rgba(59, 130, 246, 0.85); }
    100% { box-shadow: 0 0 8px 3px rgba(59, 130, 246, 0.95); }
  }
  .chatsdet-highlight {
    position: fixed;
    pointer-events: none;
    z-index: 999999;
  }
  .chatsdet-highlight--click {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${hexToRgba(RIPPLE_COLOR, 0.45)};
    border: 4px solid ${RIPPLE_COLOR};
    animation: chatsdet-ripple 380ms ease-out forwards;
  }
  .chatsdet-highlight--type {
    border-radius: 4px;
    border: 3px solid ${RIPPLE_COLOR};
    background: rgba(59, 130, 246, 0.18);
    animation: chatsdet-glow 520ms ease-in-out;
  }
  .chatsdet-highlight--hover {
    border: 3px dashed #8b5cf6;
    background: rgba(139, 92, 246, 0.2);
    border-radius: 4px;
  }
  .chatsdet-highlight--halo {
    border-radius: 6px;
    border: 3px solid ${RIPPLE_COLOR};
    background: ${hexToRgba(RIPPLE_COLOR, 0.14)};
    animation: chatsdet-halo-in 140ms ease-out;
  }
`;
}

/**
 * Keycap tray styles for the anticipatory cue runtime.
 *
 * Rendered only inside the overlay's closed shadow root, so these class names can
 * never collide with application CSS and the cap text can never be reached by a
 * light-DOM text query. Kept out of {@link buildHighlightCss} because the
 * driver-driven {@link CursorOverlay}, which injects into the page document, has
 * no keycaps — only the shadow-root runtime does.
 */
export function buildKeycapCss(): string {
  return `
  @keyframes chatsdet-keycap-press {
    0%   { transform: translateY(0);   box-shadow: 0 4px 0 0 rgba(15, 23, 42, 0.55); }
    45%  { transform: translateY(3px); box-shadow: 0 1px 0 0 rgba(15, 23, 42, 0.55); }
    100% { transform: translateY(0);   box-shadow: 0 4px 0 0 rgba(15, 23, 42, 0.55); }
  }
  @keyframes chatsdet-keycap-fade {
    0%, 70% { opacity: 1; }
    100%    { opacity: 0; }
  }
  .chatsdet-keycap-tray {
    position: fixed;
    left: 50%;
    bottom: 32px;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    pointer-events: none;
    animation: chatsdet-keycap-fade 800ms ease-out forwards;
  }
  .chatsdet-keycap {
    min-width: 34px;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid rgba(15, 23, 42, 0.75);
    background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%);
    color: #0f172a;
    font: 600 14px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
    text-align: center;
    animation: chatsdet-keycap-press 180ms ease-out;
  }
`;
}
